
import { create } from 'zustand';
import { doDELETE, doGET, doPOST, doPUT } from '../../utils/HttpUtils';
import { showError } from '../toaster';
import {
    ICoupon,
    CreateCouponRequest,
    CouponQueryParams,
    CouponState,
    ValidateCouponRequest
} from '../../types/coupons';

const BASE_URL = '/coupons';
let latestGridRequest = 0;

const store = create<CouponState>((set, get) => ({
    data: [],
    totalPages: 0,
    total: 0,
    currentPage: 1,
    filters: {},
    isLoading: false,
    limit: 10,

    // GET /coupons (with query params for pagination & filters)
    fetchGrid: async () => {
        const requestId = ++latestGridRequest;
        try {
            const { filters, currentPage, limit } = get();
            set({ isLoading: true });

            const cleanFilters = Object.fromEntries(
                Object.entries(filters || {}).filter(([, v]) => v !== undefined && v !== '')
            );
            const queryParams = new URLSearchParams(cleanFilters as any);
            queryParams.append('page', String(currentPage));
            queryParams.append('limit', String(limit));

            const apiUrl = `${BASE_URL}?${queryParams.toString()}`;

            const response = await doGET(apiUrl);
            if (requestId !== latestGridRequest) return;

            if (response.status >= 200 && response.status < 400) {
                const resData = response.data?.data || response.data;
                const rows = resData?.rows || resData;
                const pagination = response.data?.pagination;
                const total = pagination?.total ?? resData?.total ?? (Array.isArray(rows) ? rows.length : 0);
                set({
                    data: Array.isArray(rows) ? rows : [],
                    totalPages: pagination?.totalPages ?? Math.ceil(total / limit),
                    total,
                });
            } else {
                showError(response.message);
            }
        } catch (err) {
            if (requestId === latestGridRequest) showError('Failed to fetch coupons');
        } finally {
            if (requestId === latestGridRequest) set({ isLoading: false });
        }
    },

    setFilters: (newFilters: CouponQueryParams) => {
        set({ filters: newFilters, currentPage: 1, data: [], total: 0, totalPages: 0 });
        get().fetchGrid();
    },

    nextPage: () => {
        const { currentPage, totalPages, fetchGrid } = get();
        if (currentPage < totalPages) {
            set(state => ({
                currentPage: state.currentPage + 1
            }));
            fetchGrid();
        }
    },

    prevPage: () => {
        const { currentPage, fetchGrid } = get();
        if (currentPage > 1) {
            set(state => ({
                currentPage: state.currentPage - 1
            }));
            fetchGrid();
        }
    },

    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        const { totalPages } = get();
        const newPage = page + 1;
        if (newPage >= 1 && newPage <= totalPages) {
            set({
                currentPage: newPage
            });
            get().fetchGrid();
        }
    },

    // POST /coupons
    onCreate: async (data: CreateCouponRequest) => {
        try {
            const response = await doPOST(BASE_URL, data);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
            } else {
                showError(response.message || 'Failed to create coupon');
            }
        } catch (error) {
            showError('Failed to create coupon');
        }
    },

    // PUT /coupons/:id
    onUpdate: async (data: ICoupon) => {
        try {
            const id = data._id;
            const response = await doPUT(`${BASE_URL}/${id}`, data);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
            } else {
                showError(response.message || 'Failed to update coupon');
            }
        } catch (error) {
            showError('Failed to update coupon');
        }
    },

    // DELETE /coupons/:id
    onDelete: async (id: string) => {
        try {
            const response = await doDELETE(`${BASE_URL}/${id}`);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
            } else {
                showError(response.message || 'Failed to delete coupon');
            }
        } catch (error) {
            showError('Failed to delete coupon');
        }
    },

    // GET /coupons/:id
    detail: async (id: string) => {
        try {
            const response = await doGET(`${BASE_URL}/${id}`);
            if (response.status >= 200 && response.status < 400) {
                return response.data?.data || response.data;
            } else {
                showError(response.message || 'Failed to fetch coupon details');
                return null;
            }
        } catch (error) {
            showError('Failed to fetch coupon details');
            return null;
        }
    },

    // GET /coupons/active
    fetchActive: async () => {
        try {
            const response = await doGET(`${BASE_URL}/active`);
            if (response.status >= 200 && response.status < 400) {
                return response.data?.data || response.data;
            } else {
                showError(response.message || 'Failed to fetch active coupons');
                return [];
            }
        } catch (error) {
            showError('Failed to fetch active coupons');
            return [];
        }
    },

    // GET /coupons/applicable?bookingType=...
    fetchApplicable: async (bookingType: string) => {
        try {
            const response = await doGET(`${BASE_URL}/applicable?bookingType=${bookingType}`);
            if (response.status >= 200 && response.status < 400) {
                return response.data?.data || response.data;
            } else {
                showError(response.message || 'Failed to fetch applicable coupons');
                return [];
            }
        } catch (error) {
            showError('Failed to fetch applicable coupons');
            return [];
        }
    },

    // POST /coupons/validate
    validateCoupon: async (data: ValidateCouponRequest) => {
        try {
            const response = await doPOST(`${BASE_URL}/validate`, data);
            if (response.status >= 200 && response.status < 400) {
                return response.data;
            } else {
                showError(response.message || 'Coupon validation failed');
                return null;
            }
        } catch (error) {
            showError('Failed to validate coupon');
            return null;
        }
    },

    // GET /coupons/code/:code
    fetchByCode: async (code: string) => {
        try {
            const response = await doGET(`${BASE_URL}/code/${code}`);
            if (response.status >= 200 && response.status < 400) {
                return response.data;
            } else {
                showError(response.message || 'Failed to fetch coupon by code');
                return null;
            }
        } catch (error) {
            showError('Failed to fetch coupon by code');
            return null;
        }
    },

    fetchUsageByCode: async (code: string, page = 1, limit = 20) => {
        try {
            const response = await doGET(`${BASE_URL}/usage/${code}?page=${page}&limit=${limit}`);
            if (response.status >= 200 && response.status < 400) {
                return {
                    data: response.data?.data || response.data,
                    pagination: response.data?.pagination,
                };
            } else {
                showError(response.message || 'Failed to fetch coupon usage');
                return null;
            }
        } catch (error) {
            showError('Failed to fetch coupon usage');
            return null;
        }
    },

    fetchAllUsageByCode: async (code: string) => {
        const pageSize = 100;
        const firstPage = await get().fetchUsageByCode(code, 1, pageSize);
        if (!firstPage) return null;

        const users = [...(firstPage.data.users || [])];
        const totalPages = firstPage.pagination?.totalPages || 1;

        for (let page = 2; page <= totalPages; page += 1) {
            const response = await get().fetchUsageByCode(code, page, pageSize);
            if (!response) return null;
            users.push(...(response.data.users || []));
        }

        return {
            ...firstPage,
            data: {
                ...firstPage.data,
                users,
            },
        };
    },
}));

export const useCouponStore = () => store((state) => state);
